/**
 * Whether this screen can be trusted right now.
 *
 * The loud half of the freshness contract, and only the loud half. Fresh, it
 * says nothing: the status strip states the account's age once, permanently,
 * and repeating it here made a screen with five clocks on it. Stale, it is a
 * bar saying how old the figures are, what budget they blew, and what is most
 * likely wrong — because "данные
 * устарели" on its own tells the owner to stop trusting the screen and nothing
 * else, while "устарели на 7 минут при бюджете 2 минуты, биржа не отвечает"
 * tells him whether to wait or to look.
 *
 * The figures are never hidden. Old numbers are still the last thing that was
 * true, and replacing them with a spinner replaces a known past with nothing at
 * all. What changes is that they stop being able to pass for current: every
 * panel fed by this reading carries the same marker, per §8.5.
 */
import { CircleAlert } from "lucide-react";

import { clockOf, humanAge } from "@/shared/time/age";

type Props = {
  readonly asOf: string;
  readonly stale: boolean;
  readonly ageSeconds: number;
  readonly budgetSeconds: number;
  /** What the venue link is doing, when it explains the delay. */
  readonly connection?: string;
};

/**
 * The likeliest cause, named rather than guessed at by the reader.
 *
 * Only what can actually be told apart from here: a venue that is not
 * answering explains a stale reading, and a venue that is answering means the
 * delay is on our side. Anything more specific would be invention.
 */
function because(connection: string | undefined): string {
  if (connection === "DISCONNECTED") return "биржа не отвечает";
  if (connection === "DEGRADED") return "свежая сверка недоступна";
  if (connection === "UNKNOWN") return "связь со счётом не подтверждена";
  return "тик не обновил счёт";
}

export function Freshness({
  asOf,
  stale,
  ageSeconds,
  budgetSeconds,
  connection,
}: Props): React.JSX.Element | null {
  // Nothing at all when the data is current. The status strip already carries
  // the account's age and never scrolls away, so a second line saying the same
  // thing is one of five clocks on a screen that needs one.
  if (!stale) return null;

  return (
    <p
      data-testid="freshness"
      data-stale="true"
      // Announced, because the figures under it did not change when it
      // appeared and a reader watching the numbers would not notice.
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-row border border-status-warning bg-surface-default px-2.5 py-2 text-body-main text-status-warning"
    >
      <CircleAlert aria-hidden="true" className="size-4 shrink-0" />
      <span className="font-medium">Данные устарели на {humanAge(ageSeconds)}</span>
      <span className="text-text-secondary">
        последняя синхронизация <time dateTime={asOf}>{clockOf(asOf)}</time> · допуск{" "}
        {humanAge(budgetSeconds)} · {because(connection)}
      </span>
    </p>
  );
}
